"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import {
  FiTruck,
  FiFileText,
  FiCalendar,
  FiMapPin,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiPlus,
  FiArrowRight,
  FiDollarSign,
  FiHome,
  FiActivity,
  FiTrendingUp,
  FiBarChart2,
  FiZap,
  FiTarget,
} from "react-icons/fi"
import { motion } from "framer-motion"
import { api } from "../../services/api"

export default function ClientHome() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [profile, setProfile] = useState(null)
  const [moves, setMoves] = useState([])
  const [quotes, setQuotes] = useState([])
  const realtimeRefreshTimerRef = useRef(null)

  const loadDashboard = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError("")
      const [p, m, q] = await Promise.all([
        api.getClientProfile().catch(() => null),
        api.getClientMoves().catch(() => []),
        api.getClientQuotes().catch(() => []),
      ])
      
      const formattedMoves = Array.isArray(m) ? m : [];
      setProfile(p || null)
      setMoves(formattedMoves)
      setQuotes(Array.isArray(q) ? q : q?.data || [])
    } catch (e) {
      setError("Impossible de charger vos données pour le moment.")
      if (String(e?.message || "").includes("401")) {
        localStorage.removeItem("token")
        localStorage.removeItem("user")
        localStorage.removeItem("userType")
        navigate("/login")
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    loadDashboard(false)
  }, [loadDashboard])

  useEffect(() => {
    const relevantEvents = new Set([
      "price_proposed",
      "status_updated",
      "negotiation_accepted",
      "service_request_created",
      "new_service_request",
      "price_accepted",
      "notification",
      "chat_created",
      "new_message",
    ])
    const onRealtime = (evt) => {
      const eventName = evt?.detail?.event
      const payload = evt?.detail?.payload
      if (!relevantEvents.has(eventName)) return
      if (
        eventName === "notification" &&
        !["price_proposed", "status_updated", "negotiation_accepted", "new_service_request", "price_accepted", "chat_message"].includes(
          payload?.type
        )
      ) {
        return
      }
      if (realtimeRefreshTimerRef.current) clearTimeout(realtimeRefreshTimerRef.current)
      realtimeRefreshTimerRef.current = setTimeout(() => {
        loadDashboard(true)
      }, 300)
    }
    window.addEventListener("liberty:realtime", onRealtime)
    return () => {
      window.removeEventListener("liberty:realtime", onRealtime)
      if (realtimeRefreshTimerRef.current) {
        clearTimeout(realtimeRefreshTimerRef.current)
        realtimeRefreshTimerRef.current = null
      }
    }
  }, [loadDashboard])

  const safeParseUser = () => {
    try {
      const rawUser = localStorage.getItem("user")
      if (!rawUser || rawUser === "undefined" || rawUser === "null") return {}
      const parsed = JSON.parse(rawUser)
      return parsed && typeof parsed === "object" ? parsed : {}
    } catch (error) {
      console.warn("[ClientHome] Impossible de parser l'utilisateur localStorage:", error)
      return {}
    }
  }

  const userFallback = safeParseUser()
  const firstName = profile?.first_name || userFallback.first_name || ""
  const lastName = profile?.last_name || userFallback.last_name || ""
  const userEmail = profile?.email || userFallback.email || ""
  const userPhone = profile?.phone || userFallback.phone || ""
  const userAddress = profile?.address || userFallback.address || ""

  const isProfileComplete = firstName && lastName && userEmail && userPhone && userAddress
  const missingFields = []
  if (!firstName || !lastName) missingFields.push("nom complet")
  if (!userPhone) missingFields.push("téléphone")
  if (!userAddress) missingFields.push("adresse")

  /** Devis / missions « en cours » sans exiger une ligne `moves` (souvent absent après acceptation app). */
  const quoteBackedActiveStatuses = useMemo(
    () =>
      new Set([
        "accepted",
        "in_progress",
        "collection_completed",
        "offer_accepted",
        "scheduled",
      ]),
    []
  )

  const parseMoveDay = (row) => {
    if (!row || typeof row !== "object") return null
    const raw = row.move_date || row.date || row.scheduled_date
    if (raw == null || raw === "") return null
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const mergedMovesForHome = useMemo(() => {
    const list = Array.isArray(moves) ? moves.filter(Boolean).map((m) => ({ ...m })) : []
    const seenQuoteIds = new Set(
      list.map((m) => m.quote_id || m.quoteId).filter(Boolean).map((id) => String(id))
    )
    const qArr = Array.isArray(quotes) ? quotes : []
    for (const q of qArr) {
      if (!q?.id) continue
      const st = String(q.status || "").toLowerCase()
      if (!quoteBackedActiveStatuses.has(st)) continue
      const qid = String(q.id)
      if (seenQuoteIds.has(qid)) continue
      seenQuoteIds.add(qid)
      list.push({
        id: q.id,
        quote_id: q.id,
        move_date: q.move_date,
        date: q.move_date,
        from_address: q.from_address,
        to_address: q.to_address,
        from_city: q.from_city,
        to_city: q.to_city,
        status: q.status,
        created_at: q.created_at,
        _fromQuote: true,
      })
    }
    return list
  }, [moves, quotes, quoteBackedActiveStatuses])

  const startOfToday = () => {
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    return t
  }

  const upcomingMove = useMemo(() => {
    const list = mergedMovesForHome
    if (!list.length) return null
    const today0 = startOfToday()
    const enriched = list.map((m) => ({ m, day: parseMoveDay(m) }))
    const future = enriched
      .filter(({ day }) => day && day >= today0)
      .sort((a, b) => a.day - b.day)
    if (future.length) return future[0].m
    const activeStatuses = new Set([
      "accepted",
      "in_progress",
      "collection_completed",
      "scheduled",
      "pending",
      "offer_accepted",
    ])
    const active = enriched.filter(({ m }) => activeStatuses.has(String(m?.status || "").toLowerCase()))
    if (!active.length) return null
    active.sort((a, b) => {
      if (a.day && b.day) return b.day - a.day
      if (a.day) return -1
      if (b.day) return 1
      const ca = new Date(a.m.created_at || 0).getTime()
      const cb = new Date(b.m.created_at || 0).getTime()
      return cb - ca
    })
    return active[0].m
  }, [mergedMovesForHome])

  // S'assurer que moves est un tableau avant d'appeler les méthodes de tableau
  const completedMoves = Array.isArray(moves) 
    ? moves.filter((m) => m && m.status === "completed").length 
    : 0;
    
  const pendingQuotes = Array.isArray(quotes) 
    ? quotes.filter((q) => q && q.status === "pending").length 
    : 0;
    
  const totalSpent = Array.isArray(moves) 
    ? moves.reduce((sum, move) => sum + (move?.total_cost || 0), 0)
    : 0;

  // Calculs supplémentaires pour les nouvelles fonctionnalités
  const activeMoves = mergedMovesForHome.filter((m) => {
    const st = String(m?.status || "").toLowerCase()
    return st === "in_progress" || st === "pending" || st === "accepted" || st === "scheduled" || st === "collection_completed"
  }).length

  const averageMoveCost = completedMoves > 0 
    ? totalSpent / completedMoves 
    : 0;

  const acceptedQuotes = Array.isArray(quotes) 
    ? quotes.filter((q) => q && q.status === "accepted").length 
    : 0;

  const quotesConversionRate = quotes.length > 0 
    ? ((acceptedQuotes / quotes.length) * 100).toFixed(1) 
    : 0;

  const upcomingMoveDay = upcomingMove ? parseMoveDay(upcomingMove) : null
  const daysUntilMove =
    upcomingMoveDay != null && !Number.isNaN(upcomingMoveDay.getTime())
      ? Math.ceil((upcomingMoveDay.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : null
  const upcomingShortDateLabel =
    upcomingMoveDay && !Number.isNaN(upcomingMoveDay.getTime())
      ? upcomingMoveDay.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })
      : null

  // Statistiques mensuelles
  const currentMonth = new Date().getMonth()
  const currentYear = new Date().getFullYear()
  const monthlyMoves = Array.isArray(moves) 
    ? moves.filter((m) => {
        const moveDate = new Date(m.move_date || m.date)
        return moveDate.getMonth() === currentMonth && moveDate.getFullYear() === currentYear
      }).length 
    : 0;

  const monthlySpent = Array.isArray(moves) 
    ? moves
        .filter((m) => {
          const moveDate = new Date(m.move_date || m.date)
          return moveDate.getMonth() === currentMonth && moveDate.getFullYear() === currentYear
        })
        .reduce((sum, move) => sum + (move?.total_cost || 0), 0)
    : 0;

  // Variantes d'animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="relative overflow-hidden bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-3xl p-8 border border-amber-500/20 shadow-2xl"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-yellow-500/5"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <motion.div variants={itemVariants} className="space-y-4">
                <div className="flex items-center gap-3">
                  <motion.div 
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center shadow-lg"
                  >
                    <FiHome className="w-6 h-6 text-gray-900" />
                  </motion.div>
                  <div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-white bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
                      Bonjour {firstName} {lastName}
                    </h1>
                    <p className="text-amber-400 font-medium flex items-center gap-2">
                      <FiActivity className="w-4 h-4" />
                      Tableau de bord Liberty Déménagement
                    </p>
                  </div>
                </div>

                {!isProfileComplete && (
                  <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <FiAlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-amber-300 font-medium">Profil incomplet</p>
                      <p className="text-amber-400/80 text-sm">Complétez : {missingFields.join(", ")}</p>
                    </div>
                    <Link
                      to="/client/profile"
                      className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 px-4 py-2 rounded-lg text-sm font-bold transition-all"
                    >
                      Compléter
                    </Link>
                  </div>
                )}
              </motion.div>

              <motion.div variants={itemVariants} className="mt-6 lg:mt-0 grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-gradient-to-br from-amber-800/20 to-amber-600/20 rounded-xl p-4 border border-amber-500/20 hover:border-amber-400/40 transition-all group cursor-pointer shadow-lg"
                  onClick={() => navigate('/client/moves')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <FiTruck className="w-5 h-5 text-amber-400 opacity-50" />
                    <motion.div 
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-2 h-2 bg-amber-400 rounded-full"
                    />
                  </div>
                  <div className="text-2xl lg:text-3xl font-bold text-amber-400">
                    {Array.isArray(moves) ? moves.length : 0}
                  </div>
                  <div className="text-gray-400 text-sm">Déménagements</div>
                </motion.div>
                
                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-gradient-to-br from-blue-800/20 to-blue-600/20 rounded-xl p-4 border border-blue-500/20 hover:border-blue-400/40 transition-all group cursor-pointer shadow-lg"
                  onClick={() => navigate('/client/quotes')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <FiFileText className="w-5 h-5 text-blue-400 opacity-50" />
                    {pendingQuotes > 0 && (
                      <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingQuotes}</span>
                    )}
                  </div>
                  <div className="text-2xl lg:text-3xl font-bold text-blue-400">{quotes.length}</div>
                  <div className="text-gray-400 text-sm">Devis</div>
                </motion.div>
                
                <motion.div 
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-gradient-to-br from-green-800/20 to-green-600/20 rounded-xl p-4 border border-green-500/20 hover:border-green-400/40 transition-all group cursor-pointer shadow-lg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <FiCheckCircle className="w-5 h-5 text-green-400 opacity-50" />
                    <div className="text-xs text-green-400 font-medium">{quotesConversionRate}%</div>
                  </div>
                  <div className="text-2xl lg:text-3xl font-bold text-green-400">{completedMoves}</div>
                  <div className="text-gray-400 text-sm">Terminés</div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {loading && (
          <div className="flex items-center justify-center gap-3 text-gray-300 bg-gray-800 p-8 rounded-2xl border border-gray-700">
            <div className="animate-spin h-8 w-8 border-3 border-gray-600 border-t-amber-400 rounded-full"></div>
            <span className="text-lg">Chargement de votre tableau de bord...</span>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-6 py-4 rounded-2xl text-center">
            <FiAlertCircle className="w-6 h-6 mx-auto mb-2" />
            {error}
          </div>
        )}

        {!loading && !error && (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-amber-500/50 p-6 rounded-2xl transition-all hover:scale-105 group shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium flex items-center gap-2">
                      <FiFileText className="w-4 h-4" />
                      Devis en attente
                    </p>
                    <p className="text-3xl font-bold text-amber-400 mt-2">{pendingQuotes}</p>
                    <p className="text-gray-500 text-xs mt-1">À traiter</p>
                  </div>
                  <motion.div 
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                  >
                    <FiFileText className="w-6 h-6 text-amber-500" />
                  </motion.div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-amber-500/50 p-6 rounded-2xl transition-all hover:scale-105 group shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium flex items-center gap-2">
                      <FiCalendar className="w-4 h-4" />
                      Prochain déménagement
                    </p>
                    <p className="text-lg font-bold text-amber-400 mt-2">
                      {upcomingMove ? upcomingShortDateLabel || "À planifier" : "Aucun"}
                    </p>
                    {daysUntilMove !== null && daysUntilMove > 0 && (
                      <p className="text-amber-400 text-xs mt-1 font-medium">
                        Dans {daysUntilMove} jour{daysUntilMove > 1 ? 's' : ''}
                      </p>
                    )}
                    {upcomingMove && (daysUntilMove == null || daysUntilMove <= 0) && (
                      <p className="text-gray-500 text-xs mt-1">
                        {["in_progress", "collection_completed"].includes(String(upcomingMove.status || "").toLowerCase())
                          ? "En cours"
                          : "Mission acceptée"}
                      </p>
                    )}
                  </div>
                  <motion.div 
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                  >
                    <FiCalendar className="w-6 h-6 text-amber-500" />
                  </motion.div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-amber-500/50 p-6 rounded-2xl transition-all hover:scale-105 group shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium flex items-center gap-2">
                      <FiDollarSign className="w-4 h-4" />
                      Total dépensé
                    </p>
                    <p className="text-2xl font-bold text-amber-400 mt-2">{totalSpent.toLocaleString('fr-TN')} TND</p>
                    <p className="text-gray-500 text-xs mt-1">Tous les déménagements</p>
                  </div>
                  <motion.div 
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                  >
                    <FiDollarSign className="w-6 h-6 text-amber-500" />
                  </motion.div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-amber-500/50 p-6 rounded-2xl transition-all hover:scale-105 group shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium flex items-center gap-2">
                      <FiTrendingUp className="w-4 h-4" />
                      Ce mois
                    </p>
                    <p className="text-2xl font-bold text-amber-400 mt-2">{monthlyMoves}</p>
                    <p className="text-gray-500 text-xs mt-1">{monthlySpent.toLocaleString('fr-TN')} TND</p>
                  </div>
                  <motion.div 
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                  >
                    <FiBarChart2 className="w-6 h-6 text-amber-500" />
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Nouvelle section : Statistiques avancées */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6 shadow-xl">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FiTarget className="w-5 h-5 text-amber-500" />
                  Performance
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Taux de conversion devis</span>
                      <span className="text-amber-400 font-bold">{quotesConversionRate}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${quotesConversionRate}%` }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className="bg-gradient-to-r from-amber-500 to-yellow-500 h-2 rounded-full"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Déménagements actifs</span>
                      <span className="text-blue-400 font-bold">{activeMoves}</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${moves.length > 0 ? (activeMoves / moves.length) * 100 : 0}%` }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full"
                      />
                    </div>
                  </div>
                  {averageMoveCost > 0 && (
                    <div className="pt-4 border-t border-gray-700">
                      <p className="text-gray-400 text-sm">Coût moyen par déménagement</p>
                      <p className="text-2xl font-bold text-amber-400 mt-1">{averageMoveCost.toLocaleString('fr-TN')} TND</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6 shadow-xl">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FiZap className="w-5 h-5 text-amber-500" />
                  Actions rapides
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/client/search"
                    className="group bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-gray-900 p-4 rounded-xl transition-all transform hover:scale-105 text-center shadow-lg"
                  >
                    <FiPlus className="w-6 h-6 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                    <div className="font-bold text-sm">Nouveau devis</div>
                  </Link>

                  <Link
                    to="/client/moves"
                    className="group bg-gray-900 border border-gray-600 hover:border-amber-500/50 text-white p-4 rounded-xl hover:bg-gray-700 transition-all transform hover:scale-105 text-center"
                  >
                    <FiTruck className="w-6 h-6 mx-auto mb-2 text-amber-500 group-hover:scale-110 transition-transform" />
                    <div className="font-bold text-sm">Mes déménagements</div>
                  </Link>

                  <Link
                    to="/client/quotes"
                    className="group bg-gray-900 border border-gray-600 hover:border-amber-500/50 text-white p-4 rounded-xl hover:bg-gray-700 transition-all transform hover:scale-105 text-center"
                  >
                    <FiFileText className="w-6 h-6 mx-auto mb-2 text-amber-500 group-hover:scale-110 transition-transform" />
                    <div className="font-bold text-sm">Mes devis</div>
                  </Link>

                  <Link
                    to="/client/search"
                    className="group bg-gray-900 border border-gray-600 hover:border-amber-500/50 text-white p-4 rounded-xl hover:bg-gray-700 transition-all transform hover:scale-105 text-center"
                  >
                    <FiTarget className="w-6 h-6 mx-auto mb-2 text-amber-500 group-hover:scale-110 transition-transform" />
                    <div className="font-bold text-sm">Rechercher</div>
                  </Link>
                </div>
              </div>
            </motion.div>


            <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <FiTruck className="w-6 h-6 text-amber-500" />
                  Prochain déménagement
                </h2>
                <Link
                  to="/client/moves"
                  className="text-amber-400 hover:text-amber-300 font-medium flex items-center gap-2 transition-colors"
                >
                  Voir tout
                  <FiArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {upcomingMove ? (
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 border border-amber-500/30 rounded-xl p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FiMapPin className="w-6 h-6 text-amber-500" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-lg text-white mb-2">
                            {upcomingMove.from_city || upcomingMove.from_address || "Départ"}
                            <span className="mx-3 text-amber-400">→</span>
                            {upcomingMove.to_city || upcomingMove.to_address || "Arrivée"}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-300">
                            {upcomingMoveDay && !Number.isNaN(upcomingMoveDay.getTime()) ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <FiCalendar className="w-4 h-4 text-amber-500" />
                                  {upcomingMoveDay.toLocaleDateString("fr-FR", {
                                    weekday: "long",
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  })}
                                </div>
                                <div className="flex items-center gap-2">
                                  <FiClock className="w-4 h-4 text-amber-500" />
                                  {upcomingMoveDay.toLocaleTimeString("fr-FR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </>
                            ) : (
                              <div className="flex items-center gap-2 text-gray-400">
                                <FiCalendar className="w-4 h-4 text-amber-500" />
                                Date de déménagement à préciser sur le devis
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center mb-3">
                        <FiCheckCircle className="w-8 h-8 text-gray-900" />
                      </div>
                      <span className="text-amber-400 font-bold">
                        {["in_progress", "collection_completed"].includes(String(upcomingMove.status || "").toLowerCase())
                          ? "En cours"
                          : "Confirmé"}
                      </span>
                      <span className="text-gray-400 text-sm mt-1">
                        {upcomingMove._fromQuote ? "Voir le devis pour le détail" : "Équipe assignée"}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiCalendar className="w-10 h-10 text-gray-500" />
                  </div>
                  <p className="text-lg font-medium mb-2">Aucun déménagement planifié</p>
                  <p className="text-sm text-gray-500 mb-6">Commencez par demander un devis gratuit</p>
                  <Link
                    to="/client/search"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold rounded-xl transition-all"
                  >
                    <FiPlus className="w-5 h-5 mr-2" />
                    Demander un devis
                  </Link>
                </div>
              )}
            </motion.div>

            <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6 shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <FiFileText className="w-6 h-6 text-amber-500" />
                  Mes devis récents
                </h2>
                <Link
                  to="/client/quotes"
                  className="text-amber-400 hover:text-amber-300 font-medium flex items-center gap-2 transition-colors"
                >
                  Voir tout
                  <FiArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {quotes.length > 0 ? (
                <div className="space-y-3">
                  {quotes.slice(0, 5).map((quote, index) => (
                    <motion.div
                      key={quote.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={`/client/quotes/${quote.id}`}
                        className="flex items-center gap-4 p-4 bg-gray-900 rounded-xl hover:bg-gray-700 transition-all border border-gray-700 hover:border-amber-500/30 cursor-pointer hover:shadow-lg"
                      >
                      <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                        <FiFileText className="w-5 h-5 text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white truncate">
                          {quote.from_city || "Départ"} → {quote.to_city || "Arrivée"}
                        </div>
                        <div className="text-sm text-gray-400 flex items-center gap-2">
                          <FiClock className="w-3 h-3" />
                          {quote.created_at
                            ? new Date(quote.created_at).toLocaleDateString("fr-FR")
                            : "Date inconnue"}
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          quote.status === "accepted"
                            ? "bg-green-500/20 text-green-400"
                            : quote.status === "rejected"
                              ? "bg-red-500/20 text-red-400"
                              : "bg-amber-500/20 text-amber-400"
                        }`}
                      >
                        {quote.status === "accepted"
                          ? "Accepté"
                          : quote.status === "rejected"
                            ? "Refusé"
                            : "En attente"}
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <FiFileText className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                  <p className="font-medium">Aucun devis</p>
                  <p className="text-sm text-gray-500 mb-4">Demandez votre premier devis gratuit</p>
                  <Link
                    to="/client/search"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-medium rounded-lg transition-all"
                  >
                    <FiPlus className="w-4 h-4 mr-2" />
                    Nouveau devis
                  </Link>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
